import { ApiRoutes } from "../../routes/ApiRoutes";
import { requestService } from "../../services/RequestService";
import { BusinessTypeView } from "./view/BusinessTypeView";
import { BusinessTypeRequestDTO } from "./dto/BusinessTypeRequestDTO";
import { BusinessSelectOptionView } from "./view/BusinessSelectOptionView";
import { Page } from "../../data/Page";
import { LanguageUtils } from "../../utils/LanguageUtils";
import { BusinessTypeCreateUpdateDto } from "./dto/BusinessTypeCreateUpdateDto";
import { BusinessTypeSearchDTO } from "./dto/BusinessTypeSearchDTO";
import { BusinessTypeManagementView } from "./view/BusinessTypeManagementView";
import { BbxRequest } from "../../data/Request/BbxRequest";
import { BusinessTypeSelectOptionView } from "./view/BusinessTypeSelectOptionView";

export const BusinessTypesModelAPI = {
    /**
     * Fetches all business types.
     * @returns {Promise<BusinessTypeView[]>} Promise resolving to an array of BusinessTypeView.
     */
    findAll: async (businessTypeRequestDTO: BusinessTypeRequestDTO): Promise<BusinessTypeView[]> => {
        const response = await requestService.post(ApiRoutes.REACT_APP_URL_BUSINESS_TYPES, businessTypeRequestDTO);

        return response.data;
    },

    /**
     * Gets all business types as select options.
     * @returns {Promise<BusinessSelectOptionView[]>} Promise resolving to an array of BusinessSelectOptionView.
     */
    getAllBusinessTypes: async (): Promise<BusinessSelectOptionView[]> => {
        const response = await requestService.get(ApiRoutes.REST_API_URL_BUSINESS_TYPE_READ, {});

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Gets partner business types only (status ENABLED / ENABLED_IS_NEW, type PARTNER).
     */
    getPartnerBusinessTypes: async (): Promise<BusinessSelectOptionView[]> => {
        const response = await requestService.get(ApiRoutes.REST_API_URL_BUSINESS_TYPE_PARTNER, {});

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Gets partner business types for configuration pages
     * (related-bus-types, business-max-selection): status IN (ENABLED, ENABLED_IS_NEW),
     * excludes AFFILIATE (9998) and GENERATION_LEADS (9999).
     */
    getPartnerBusinessTypesForConfig: async (): Promise<BusinessSelectOptionView[]> => {
        const response = await requestService.get(ApiRoutes.REST_API_URL_BUSINESS_TYPE_PARTNER_FOR_CONFIG, {});

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Searches business types by name (autocomplete).
     * @param {string} name - The name to search for.
     * @returns {Promise<BusinessSelectOptionView[]>} Promise resolving to an array of BusinessSelectOptionView.
     */
    businessAutocomplete: async (name: string): Promise<BusinessSelectOptionView[]> => {
        const response = await requestService.get(
            ApiRoutes.REST_API_URL_BUSINESS_TYPE_AUTOCOMPLETE + "/" + LanguageUtils.getLanguage() + "/" + name,
            {}
        );

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Gets all business sub-categories (default list for the autocomplete dropdown).
     * @returns {Promise<BusinessSelectOptionView[]>} Promise resolving to an array of BusinessSelectOptionView.
     */
    getBusinessSubCategories: async (): Promise<BusinessSelectOptionView[]> => {
        const response = await requestService.get(
            ApiRoutes.REST_API_URL_BUSINESS_TYPE_AUTOCOMPLETE + "/" + LanguageUtils.getLanguage(),
            {}
        );

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Gets business types by their IDs.
     * @param {number[]} ids - Array of business type IDs.
     * @returns {Promise<BusinessSelectOptionView[]>} Promise resolving to an array of BusinessSelectOptionView.
     */
    getBusinessTypesByIds: async (ids: number[]): Promise<BusinessSelectOptionView[]> => {
        if (!ids || ids.length === 0) return [];

        const params = ids.map(id => `ids=${id}`).join('&');

        const response = await requestService.get(`${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/by-ids?${params}`);

        return response.data.map((item: any) => new BusinessSelectOptionView(item));
    },

    /**
     * Search for business types using BbxRequest.
     * @param {BbxRequest<BusinessTypeSearchDTO>} request - The request containing search criteria.
     * @returns {Promise<Page<BusinessTypeManagementView>>} Promise resolving to a page of business types.
     */
    search: async (request: BbxRequest<BusinessTypeSearchDTO>): Promise<Page<BusinessTypeManagementView>> => {
        const response = await requestService.post(
            `${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/management`,
            request
        );
        return new Page<BusinessTypeManagementView>(BusinessTypeManagementView, response.data);
    },

    /**
     * Get business type by ID.
     * @param {number} id - The business type ID.
     * @returns {Promise<BusinessTypeManagementView>} Promise resolving to business type data.
     */
    getById: async (id: number): Promise<BusinessTypeManagementView> => {
        const response = await requestService.get(
            `${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/get/${id}`
        );
        return new BusinessTypeManagementView(response.data);
    },

    /**
     * Create a new business type.
     * @param {BusinessTypeCreateUpdateDto} dto - The business type data.
     * @param {File} image - Optional image file.
     * @returns {Promise<BusinessTypeManagementView>} Promise resolving when creation is complete.
     */
    create: async (dto: BusinessTypeCreateUpdateDto, image?: File | null): Promise<BusinessTypeManagementView> => {
        const formData = new FormData();
        formData.append('data', new Blob([JSON.stringify(dto)], {type: 'application/json'}));
        if (image) {
            formData.append('image', image);
        }
        const response = await requestService.post(
            `${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/create`,
            formData,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );
        return new BusinessTypeManagementView(response.data);
    },

    /**
     * Update an existing business type.
     * @param {number} id - The business type ID.
     * @param {BusinessTypeCreateUpdateDto} dto - The updated business type data.
     * @param {File} image - Optional image file.
     * @returns {Promise<BusinessTypeManagementView>} Promise resolving when update is complete.
     */
    update: async (id: number, dto: BusinessTypeCreateUpdateDto, image?: File | null): Promise<BusinessTypeManagementView> => {
        const formData = new FormData();
        formData.append('data', new Blob([JSON.stringify(dto)], {type: 'application/json'}));
        if (image) {
            formData.append('image', image);
        }
        const resp = await requestService.put(
            `${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/update/${id}`,
            formData,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );
        return new BusinessTypeManagementView(resp.data);
    },

    /**
     * Get parent options for business types.
     * @returns {Promise<BusinessTypeSelectOptionView[]>} Promise resolving to parent options.
     */
    getParentOptions: async (): Promise<BusinessTypeSelectOptionView[]> => {
        if (!ApiRoutes.REST_API_URL_BUSINESS_TYPE_PARENTS) {
            throw new Error("REST_API_URL_BUSINESS_TYPE_PARENTS is not defined");
        }

        const response = await requestService.get(ApiRoutes.REST_API_URL_BUSINESS_TYPE_PARENTS);
        return response.data.map((item: any) => {
            return new BusinessTypeSelectOptionView(item);
        });
    },

    /**
     * Gets the ViteUnDevis (VUD) category id for a business type, or -1 when the category is not
     * VUD-eligible (gates the FR-forward button).
     * @param {number} businessTypeId - The business type id.
     */
    getVudId: async (businessTypeId: number): Promise<number> => {
        const response = await requestService.get(`${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/${businessTypeId}/vud-id`);
        return response.data;
    },

    /**
     * Get action classes for business types.
     * @returns {Promise<string[]>} Promise resolving to action classes.
     */
    getActionClasses: async (): Promise<string[]> => {
        const response = await requestService.get(`${ApiRoutes.REACT_APP_URL_BUSINESS_TYPES}/action-classes`);

        return response.data || [];
    },

};